/**
 * Billing calls go to the stripe service (decast-stripe-service), which verifies the same
 * session JWT the rest of the app uses. The service never takes a userId from us; it reads
 * it from the token.
 */
import axios from 'axios';
import constants from '../../../constant';
import { withAuthRetry } from '../refresh';

const http = withAuthRetry(
  axios.create({
    baseURL: `${constants.stripeServiceUrl}/api/v1`,
    timeout: 20000,
  })
);

export const PENDING_CHECKOUT_KEY = 'pendingCheckout';

/**
 * Where signing in should land someone. A checkout they started before signing in is the
 * whole reason they signed in, so it wins over wherever they would otherwise go.
 */
export function afterAuthPath(fallback = '/') {
  return localStorage.getItem(PENDING_CHECKOUT_KEY)
    ? '/dashboard/account/plans'
    : fallback;
}

/** Remember a plan through a sign-in, so nobody has to choose it twice. */
export function rememberCheckout(planId) {
  try {
    localStorage.setItem(PENDING_CHECKOUT_KEY, planId);
  } catch (e) {
    // a browser refusing storage just means they pick the plan again after signing in
  }
}

export function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || '{}') || {};
  } catch (e) {
    return {};
  }
}

export function isSignedIn() {
  const user = currentUser();
  return !!localStorage.getItem('accessToken') && !!user.id;
}

/**
 * The pricing catalogue: tiers, prices, copy and the plan ids to check out with. Public,
 * and the source the bundled pricingData only stands in for when this call fails.
 */
export function fetchCatalog() {
  return http.get('/catalog').then((r) => r.data.data);
}

export function listPlans() {
  return http.get('/plans').then((r) => r.data.data || []);
}

/** What this account is allowed: limits, tier, and whether a subscription is paying. */
export function myEntitlements() {
  return http.get('/entitlements/me').then((r) => r.data.data || r.data);
}

export function mySubscription() {
  return http.get('/subscriptions/me').then((r) => r.data.data || null);
}

// Stripe substitutes {CHECKOUT_SESSION_ID} itself; it must reach it unencoded.
export function startCheckout(planId) {
  const origin = window.location.origin;
  return http
    .post('/subscriptions/checkout', {
      planId,
      email: currentUser().email || undefined,
      successUrl: `${origin}/billing/result?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${origin}/billing/result?status=cancel`,
    })
    .then((r) => r.data.data);
}

export function openPortal(returnPath = '/pricing') {
  return http
    .post('/subscriptions/portal', {
      returnUrl: `${window.location.origin}${returnPath}`,
    })
    .then((r) => r.data.data);
}

export function errorMessage(e, fallback) {
  return (
    (e && e.response && e.response.data && e.response.data.error) || fallback
  );
}
